import logger from '../lib/logger';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Upload, Search, FileText, Image, File, Trash2, Download, ExternalLink,
  FolderOpen, Filter, X, Plus, HardDrive, Eye, Edit2, Check,
  FileSpreadsheet, FileIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const CATEGORIES = ['All', 'KYC', 'Property Document', 'Agreement', 'Payment', 'Booking', 'Brochure', 'Price List', 'Other'];

const getFileIcon = (type) => {
  if (!type) return File;
  if (type.includes('pdf')) return FileText;
  if (type.includes('image')) return Image;
  if (type.includes('sheet') || type.includes('excel') || type.includes('csv')) return FileSpreadsheet;
  return FileIcon;
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [driveConnected, setDriveConnected] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterEntity, setFilterEntity] = useState('all');
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [renameDoc, setRenameDoc] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [uploadForm, setUploadForm] = useState({ category: 'Other', entity_type: '', entity_id: '' });
  const [leads, setLeads] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const fetchAll = useCallback(async () => {
    try {
      const [statusRes, docsRes, leadsRes] = await Promise.all([
        api.get('/drive/status'),
        api.get('/drive/documents'),
        api.get('/leads').catch(() => ({ data: [] })),
      ]);
      setDriveConnected(statusRes.data.connected);
      setDocuments(docsRes.data);
      setLeads(leadsRes.data);
    } catch (e) { logger.error(e); }
  }, []);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadProgress(0);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);

      let url = `/drive/upload?category=${encodeURIComponent(uploadForm.category)}`;
      if (uploadForm.entity_type && uploadForm.entity_id) {
        url += `&entity_type=${uploadForm.entity_type}&entity_id=${uploadForm.entity_id}`;
      }

      try {
        await api.post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => { if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100)); },
        });
        toast.success(`${file.name} uploaded`);
      } catch (e) {
        toast.error(e.response?.data?.detail || `Failed to upload ${file.name}`);
      }
      setUploadProgress(Math.round(((i + 1) / files.length) * 100));
    }

    setUploading(false);
    setShowUpload(false);
    setUploadProgress(0);
    fetchAll();
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.file_name}"? The file will be moved to Google Drive trash.`)) return;
    try {
      await api.delete(`/drive/documents/${doc.id}`);
      toast.success('Document deleted');
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleRename = async () => {
    if (!renameDoc || !renameValue.trim()) return;
    try {
      await api.put(`/drive/documents/${renameDoc.id}/rename?new_name=${encodeURIComponent(renameValue)}`);
      toast.success('Renamed');
      setRenameDoc(null);
      fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(e.type === 'dragenter' || e.type === 'dragover'); };
  const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); if (e.dataTransfer.files?.length) { setShowUpload(true); handleUpload(e.dataTransfer.files); } };

  const filtered = documents.filter(d => {
    if (filterCategory !== 'All' && d.category !== filterCategory) return false;
    if (filterEntity !== 'all' && d.entity_type !== filterEntity) return false;
    if (searchTerm && !d.file_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !d.uploaded_by_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const canDelete = user?.role === 'super_admin' || user?.role === 'admin';

  return (
    <Layout>
      <div className="aavaas-page" data-testid="documents-page"
        onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
      >
        {/* Drag overlay */}
        {dragActive && (
          <div className="fixed inset-0 z-50 bg-blue-500/10 border-4 border-dashed border-blue-400 flex items-center justify-center pointer-events-none">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <Upload className="w-12 h-12 text-blue-500 mx-auto mb-3" />
              <p className="text-lg font-semibold text-slate-900">Drop files to upload</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="aavaas-page-title" data-testid="documents-title">Documents</h1>
            <p className="text-sm text-slate-500 mt-1">{filtered.length} document{filtered.length !== 1 ? 's' : ''}{driveConnected && ' — synced with Google Drive'}</p>
          </div>
          <Button className="bg-slate-900 hover:bg-slate-800 rounded-lg shadow-sm h-10 text-sm font-semibold" onClick={() => setShowUpload(true)} disabled={!driveConnected} data-testid="upload-btn">
            <Upload className="w-4 h-4 mr-1.5" /> Upload Document
          </Button>
        </div>

        {!driveConnected && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6 flex items-start gap-3">
            <HardDrive className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-800">Google Drive not connected</p>
              <p className="text-xs text-amber-700 mt-1">Ask your Super Admin to connect Google Drive in Settings → Integrations.</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-3 mb-6 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search documents..." className="pl-10 bg-slate-50 border-slate-200 h-9 text-sm rounded-lg" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="doc-search" />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[150px] h-9 text-sm bg-white border-slate-200 rounded-lg shadow-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterEntity} onValueChange={setFilterEntity}>
            <SelectTrigger className="w-[130px] h-9 text-sm bg-white border-slate-200 rounded-lg shadow-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="lead">Leads</SelectItem>
              <SelectItem value="client">Clients</SelectItem>
              <SelectItem value="deal">Deals</SelectItem>
              <SelectItem value="project">Projects</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Document Grid */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] py-16 text-center">
            <FolderOpen className="w-14 h-14 mx-auto text-slate-200 mb-3" />
            <p className="text-sm font-medium text-slate-500">No documents found</p>
            <p className="text-xs text-slate-400 mt-1">{driveConnected ? 'Upload your first document' : 'Connect Google Drive to get started'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.map(doc => {
              const Icon = getFileIcon(doc.file_type);
              return (
                <div key={doc.id} className="bg-white rounded-xl border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden card-hover group" data-testid={`doc-${doc.id}`}>
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2.5 bg-slate-50 rounded-lg flex-shrink-0 border border-slate-100">
                        <Icon className="w-5 h-5 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-slate-900 truncate" title={doc.file_name}>{doc.file_name}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-semibold">{doc.category}</span>
                          {doc.file_size && <span>{formatSize(doc.file_size)}</span>}
                        </div>
                        <div className="flex items-center gap-2 mt-1.5 text-[11px] text-slate-400">
                          <span>{doc.uploaded_by_name}</span>
                          <span className="w-0.5 h-0.5 rounded-full bg-slate-300" />
                          <span>{formatDate(doc.created_at)}</span>
                        </div>
                        {doc.entity_type && (
                          <span className="inline-block mt-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-600 border border-blue-100 capitalize">{doc.entity_type}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="flex items-stretch border-t border-slate-100 bg-slate-50/50">
                    {doc.google_drive_url && (
                      <a href={doc.google_drive_url} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-1 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors border-r border-slate-100">
                        <ExternalLink className="w-3 h-3" /> View
                      </a>
                    )}
                    <a href={`${api.defaults.baseURL}/drive/documents/${doc.id}/download`} className="flex-1 flex items-center justify-center gap-1 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors border-r border-slate-100" data-testid={`download-${doc.id}`}>
                      <Download className="w-3 h-3" /> Download
                    </a>
                    <button className="flex-1 flex items-center justify-center gap-1 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors border-r border-slate-100" onClick={() => { setRenameDoc(doc); setRenameValue(doc.file_name); }}>
                      <Edit2 className="w-3 h-3" /> Rename
                    </button>
                    {canDelete && (
                      <button className="flex-1 flex items-center justify-center gap-1 py-2 text-xs font-medium text-red-500 hover:bg-red-50 transition-colors" onClick={() => handleDelete(doc)} data-testid={`delete-doc-${doc.id}`}>
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Upload Dialog */}
        <Dialog open={showUpload} onOpenChange={setShowUpload}>
          <DialogContent className="max-w-md rounded-xl">
            <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Category</Label>
                <Select value={uploadForm.category} onValueChange={v => setUploadForm({ ...uploadForm, category: v })}>
                  <SelectTrigger className="mt-1 h-10 rounded-lg" data-testid="upload-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.filter(c => c !== 'All').map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Link to (optional)</Label>
                <Select onValueChange={v => {
                  if (v === 'none') { setUploadForm({ ...uploadForm, entity_type: '', entity_id: '' }); return; }
                  const [type, id] = v.split('::');
                  setUploadForm({ ...uploadForm, entity_type: type, entity_id: id });
                }}>
                  <SelectTrigger className="mt-1 h-10 rounded-lg"><SelectValue placeholder="General (no link)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">General (no link)</SelectItem>
                    {leads.slice(0, 30).map(l => <SelectItem key={l.id} value={`lead::${l.id}`}>Lead: {l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div
                className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-600">Click to select files</p>
                <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG, DOC, XLS, CSV — Max 50MB</p>
                <input ref={fileInputRef} type="file" multiple className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv,.pptx,.txt"
                  onChange={e => { if (e.target.files?.length) handleUpload(e.target.files); }}
                  data-testid="file-input"
                />
              </div>
              {uploading && (
                <div>
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Rename Dialog */}
        <Dialog open={!!renameDoc} onOpenChange={v => { if (!v) setRenameDoc(null); }}>
          <DialogContent className="max-w-sm rounded-xl">
            <DialogHeader><DialogTitle>Rename Document</DialogTitle></DialogHeader>
            <div className="mt-4 space-y-4">
              <Input value={renameValue} onChange={e => setRenameValue(e.target.value)} className="h-10 rounded-lg" data-testid="rename-input" />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setRenameDoc(null)}>Cancel</Button>
                <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleRename} data-testid="confirm-rename"><Check className="w-4 h-4 mr-1" /> Rename</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Documents;
